import React, { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : "Failed to change password");

function PwField({ id, label, value, onChange, show }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)}
        autoComplete="off" data-testid={id} />
    </div>
  );
}

export default function ForceChangePassword() {
  const { user, setUser, logout } = useAuth();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const mismatch = f.confirm && f.next !== f.confirm;
  const ready = f.current && f.next && f.confirm && !mismatch;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      toast.success("Password updated. Welcome back!");
      setUser({ ...user, must_change_password: false });
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="force-change-password">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-4">
        <div className="h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Icons.KeyRound className="h-6 w-6" /></div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Set a new password</h1>
          <p className="text-sm text-slate-500 mt-1">Hi {user?.full_name}, your password was reset by an administrator. Enter the reset password you received, then choose a new one.</p>
        </div>
        <PwField id="fcp-current" label="Reset Password" value={f.current} onChange={(v) => setF({ ...f, current: v })} show={show} />
        <PwField id="fcp-new" label="New Password" value={f.next} onChange={(v) => setF({ ...f, next: v })} show={show} />
        <PwField id="fcp-confirm" label="Confirm Password" value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} show={show} />
        {mismatch && <p className="text-xs text-rose-600" data-testid="fcp-mismatch">New Password and Confirm Password do not match.</p>}
        <p className="text-xs text-slate-500">At least 10 characters, using 3 of: uppercase, lowercase, number, symbol.</p>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} data-testid="fcp-show" /> Show passwords
        </label>
        <Button type="submit" disabled={!ready || busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="fcp-submit">{busy ? "Saving…" : "Save New Password"}</Button>
        <button type="button" onClick={logout} className="w-full text-xs text-slate-500 hover:text-slate-700" data-testid="fcp-logout">Sign out</button>
      </form>
    </div>
  );
}
